import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { env } from '@/config/env.ts'
import {
  allTestCases,
  testCategories,
  type TestCase,
  type TestResult,
} from '../testCases.ts'
import { cn } from '@/utils/cn.ts'

type TestStatus = 'idle' | 'running' | 'done'

interface TestState {
  status: TestStatus
  result?: TestResult
}

function makeInitialState(): Record<string, TestState> {
  return Object.fromEntries(
    allTestCases.map((t) => [t.id, { status: 'idle' as TestStatus }]),
  )
}

export function TestDashboardPage() {
  const [searchParams] = useSearchParams()
  const [tests, setTests] =
    useState<Record<string, TestState>>(makeInitialState)
  const [running, setRunning] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set(testCategories),
  )
  const autorunKeyRef = useRef<string | null>(null)

  const runSingleTest = useCallback(async (tc: TestCase) => {
    setTests((prev) => ({ ...prev, [tc.id]: { status: 'running' } }))
    const result = await tc.run()
    setTests((prev) => ({ ...prev, [tc.id]: { status: 'done', result } }))
    return result
  }, [])

  const runCategory = useCallback(
    async (category: string) => {
      setRunning(true)
      const categoryTests = allTestCases.filter((t) => t.category === category)
      try {
        for (const tc of categoryTests) {
          await runSingleTest(tc)
        }
      } finally {
        setRunning(false)
      }
    },
    [runSingleTest],
  )

  const runByTestId = useCallback(
    async (testId: string) => {
      const tc = allTestCases.find((t) => t.id === testId)
      if (!tc) return
      setRunning(true)
      try {
        await runSingleTest(tc)
      } finally {
        setRunning(false)
      }
    },
    [runSingleTest],
  )

  const runAll = useCallback(async () => {
    setRunning(true)
    setTests(makeInitialState())
    try {
      for (const tc of allTestCases) {
        await runSingleTest(tc)
      }
    } finally {
      setRunning(false)
    }
  }, [runSingleTest])

  const resetAll = useCallback(() => {
    setTests(makeInitialState())
  }, [])

  const toggleCategory = useCallback((cat: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) {
        next.delete(cat)
      } else {
        next.add(cat)
      }
      return next
    })
  }, [])

  const selectedCategory = searchParams.get('category')
  const selectedTestId = searchParams.get('test')
  const shouldAutorun = searchParams.get('autorun') === 'true'

  useEffect(() => {
    if (!selectedCategory && !selectedTestId) return

    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (selectedCategory && testCategories.includes(selectedCategory)) {
        next.add(selectedCategory)
      }

      if (selectedTestId) {
        const test = allTestCases.find((t) => t.id === selectedTestId)
        if (test) next.add(test.category)
      }
      return next
    })
  }, [selectedCategory, selectedTestId])

  useEffect(() => {
    if (!shouldAutorun) return
    if (running) return

    const runKey = `${selectedCategory ?? ''}|${selectedTestId ?? ''}|${shouldAutorun}`
    if (autorunKeyRef.current === runKey) return
    autorunKeyRef.current = runKey

    if (selectedTestId) {
      void runByTestId(selectedTestId)
      return
    }
    if (selectedCategory) {
      void runCategory(selectedCategory)
      return
    }
    void runAll()
  }, [
    shouldAutorun,
    selectedCategory,
    selectedTestId,
    running,
    runAll,
    runByTestId,
    runCategory,
  ])

  const allStates = Object.values(tests)
  const total = allStates.length
  const passed = allStates.filter((s) => s.result?.pass === true).length
  const failed = allStates.filter(
    (s) => s.status === 'done' && s.result?.pass === false,
  ).length
  const pending = allStates.filter(
    (s) => s.status === 'idle' || s.status === 'running',
  ).length
  const totalDuration = allStates.reduce(
    (sum, s) => sum + (s.result?.duration ?? 0),
    0,
  )
  const progress = total > 0 ? ((passed + failed) / total) * 100 : 0

  const getStatusIcon = (state: TestState) => {
    if (state.status === 'running')
      return <span className="inline-block animate-spin">⏳</span>
    if (state.status === 'done') return state.result?.pass ? '✅' : '❌'
    return '⚪'
  }

  const getCategoryBadge = (category: string) => {
    const catTests = allTestCases.filter((t) => t.category === category)
    const catStates = catTests.map((t) => tests[t.id])
    const anyRunning = catStates.some((s) => s.status === 'running')
    const allDone = catStates.every((s) => s.status === 'done')
    const allPassed = allDone && catStates.every((s) => s.result?.pass)
    const hasFailed = catStates.some(
      (s) => s.status === 'done' && !s.result?.pass,
    )

    const badgeBase =
      'rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap'

    if (anyRunning)
      return (
        <span className={cn(badgeBase, 'bg-yellow-500/15 text-yellow-500')}>
          running
        </span>
      )
    if (hasFailed)
      return (
        <span className={cn(badgeBase, 'bg-red-500/15 text-red-500')}>
          {catStates.filter((s) => s.result?.pass).length}/{catTests.length}{' '}
          passed
        </span>
      )
    if (allPassed)
      return (
        <span className={cn(badgeBase, 'bg-green-500/15 text-green-500')}>
          all passed
        </span>
      )
    return (
      <span className={cn(badgeBase, 'text-fg-muted bg-white/6')}>
        {catTests.length} tests
      </span>
    )
  }

  const disabledContent = (
    <div className="text-fg-muted flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
      <h2 className="text-fg-secondary text-xl">🔒 Test Runner</h2>
      <p>
        Test runner is only available in the <code>development</code> build
      </p>
    </div>
  )

  return !env.enableDebug ? (
    disabledContent
  ) : (
    <div className="mx-auto min-h-screen max-w-[960px] px-4 py-8 md:px-6">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-fg flex items-center gap-2 text-[1.75rem] font-bold">
            🧪 Test Runner
          </h1>
          <div className="text-fg-muted mt-1 text-sm">
            In-browser smoke tests • {env.appEnv} environment •{' '}
            {new Date().toLocaleString()}
          </div>
          <div className="text-fg-muted mt-1 text-xs">
            Deep links: <code>?category=Stress%20Tests&amp;autorun=true</code>{' '}
            or <code>?test=stress-concurrent-api&amp;autorun=true</code>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="border-line text-fg-secondary hover:border-fg-muted hover:text-fg cursor-pointer rounded-md border bg-transparent px-4 py-2 text-[0.8125rem] transition-all duration-200 disabled:opacity-60"
            onClick={resetAll}
            disabled={running}
          >
            Reset
          </button>
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-1 rounded-md border-none bg-gradient-to-br from-indigo-500 to-violet-500 px-6 py-2 text-sm font-semibold text-white shadow-none transition-all duration-200 hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(99,102,241,0.4)] disabled:cursor-not-allowed disabled:opacity-60"
            onClick={runAll}
            disabled={running}
          >
            {running ? (
              <>
                <span className="inline-block animate-spin">⏳</span> Running...
              </>
            ) : (
              '▶ Run All Tests'
            )}
          </button>
        </div>
      </div>

      <div className="mb-8 h-[3px] w-full overflow-hidden rounded-sm bg-white/5">
        <div
          className="h-full rounded-sm bg-gradient-to-r from-indigo-500 to-green-500 transition-[width] duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="border-line bg-surface-raised mb-8 flex flex-wrap gap-6 rounded-md border px-6 py-4">
        <div className="text-fg-secondary flex items-center gap-1 text-sm">
          <span className="text-fg text-lg font-bold">{total}</span>
          Total
        </div>
        <div className="text-fg-secondary flex items-center gap-1 text-sm">
          <span className="text-lg font-bold text-green-500">{passed}</span>
          Passed
        </div>
        <div className="text-fg-secondary flex items-center gap-1 text-sm">
          <span className="text-lg font-bold text-red-500">{failed}</span>
          Failed
        </div>
        <div className="text-fg-secondary flex items-center gap-1 text-sm">
          <span className="text-lg font-bold text-yellow-500">{pending}</span>
          Pending
        </div>
        {totalDuration > 0 && (
          <div className="ml-auto font-mono text-[0.8125rem] text-violet-500">
            {totalDuration.toFixed(0)}ms
          </div>
        )}
      </div>

      {testCategories.map((category) => {
        const catTests = allTestCases.filter((t) => t.category === category)
        const expanded = expandedCategories.has(category)
        const catRunning = catTests.some(
          (t) => tests[t.id].status === 'running',
        )

        return (
          <div
            key={category}
            className="border-line bg-surface-raised mb-6 overflow-hidden rounded-md border transition-[border-color] duration-200 hover:border-indigo-500/30"
          >
            <div
              className="flex cursor-pointer items-center justify-between px-6 py-4 transition-colors duration-150 select-none hover:bg-white/[0.02]"
              onClick={() => toggleCategory(category)}
              onKeyDown={(e) => e.key === 'Enter' && toggleCategory(category)}
              role="button"
              tabIndex={0}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-fg-muted w-4 text-xs transition-transform duration-200',
                    expanded && 'rotate-90',
                  )}
                >
                  ▸
                </span>
                <span className="text-fg text-[0.9375rem] font-semibold">
                  {category}
                </span>
                {getCategoryBadge(category)}
              </div>
              <button
                type="button"
                className="border-line text-fg-secondary cursor-pointer rounded-sm border bg-transparent px-3 py-1 text-xs transition-all duration-150 hover:border-indigo-500 hover:text-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={running || catRunning}
                onClick={(e) => {
                  e.stopPropagation()
                  runCategory(category)
                }}
              >
                {catRunning ? 'Running...' : 'Run'}
              </button>
            </div>

            {expanded && (
              <div className="border-line border-t">
                {catTests.map((tc) => {
                  const state = tests[tc.id]
                  return (
                    <div
                      key={tc.id}
                      className="flex items-start gap-2 border-b border-white/[0.03] px-6 py-2 text-[0.8125rem] transition-colors duration-100 last:border-b-0 hover:bg-white/[0.015]"
                    >
                      <div className="mt-px flex h-5 w-5 shrink-0 items-center justify-center text-sm">
                        {getStatusIcon(state)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-fg font-medium">{tc.name}</div>
                        {state.result?.error && (
                          <div className="mt-1 rounded-sm bg-red-500/10 px-2 py-1 font-mono text-xs break-words whitespace-pre-wrap text-red-300">
                            {state.result.error}
                          </div>
                        )}
                      </div>
                      {state.result?.duration !== undefined && (
                        <div className="text-fg-muted mt-0.5 shrink-0 font-mono text-xs">
                          {state.result.duration.toFixed(0)}ms
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
